import type { Json } from "@liveblocks/client";
import { isReadOnlyDocRole } from "@/components/access/access-rules";
import { FEATURE_FLAGS } from "@/lib/feature-flags/feature-flags.constant";
import { isOnFeatureFlag } from "@/lib/feature-flags/snapshot";
import { captureSentryError } from "@/shared/utils/sentry-helpers";
import type { YjsCollaborationBridge } from "@/features/collaboration/yjs/types";
import type { StoryStateType } from "../types";

declare global {
  interface Window {
    SD_YJS_COLLABORATION?: YjsCollaborationBridge;
  }
}

/**
 * Send flow P5: viewers and commenters never push edits to the room, whatever
 * UI path dispatched them (drag, resize, shortcut). Flag off = today.
 */
const isReadOnly = (state: Pick<StoryStateType, "docAccess">) =>
  isReadOnlyDocRole(state.docAccess?.myRole) &&
  isOnFeatureFlag(FEATURE_FLAGS.SEND_FLOW);

export const broadcastEvent = ({
  state,
  payload,
  event,
}: {
  /** The story slice state of the dispatching reducer (its caller's role). */
  state: Pick<StoryStateType, "docAccess">;
  payload: Record<string, unknown>;
  event: string;
}) => {
  if (isReadOnly(state)) return;
  const bridge = window.SD_YJS_COLLABORATION;
  const room = window.SD_LIVE_ROOM;

  if (bridge?.enabled) {
    const applied = bridge.applyEvent(event, payload);
    if (!applied) {
      captureSentryError(new Error(`[COLLAB] Unmapped Yjs event: ${event}`), {
        tags: { feature: "collaboration", subsystem: "yjs-bridge" },
        extra: { event, payloadKeys: Object.keys(payload || {}) },
      });
    }
    return;
  }

  if (room) {
    try {
      const self = room.getSelf();
      room.broadcastEvent({
        type: event,
        // CRITICAL: broadcastEvent runs INSIDE the edit reducers, so `payload`
        // can hold live immer-draft proxies (e.g. createEntityFactory's
        // `created`, passed as broadcastPayload). Liveblocks does NOT serialize
        // synchronously — it queues the message and flushes it ~16ms later
        // (throttle). By then immer has finalized the reducer and REVOKED those
        // draft proxies, so Liveblocks' async JSON.stringify throws "Cannot
        // perform 'get' on a proxy that has been revoked" — which poisons the
        // whole outgoing queue, so every later broadcast AND cursor-presence
        // flush fails too (the persistent error spam + broken saves). Snapshot to
        // plain JSON NOW, while the drafts are still readable, so only detached,
        // serializable data ever reaches the async queue.
        payload: JSON.parse(JSON.stringify(payload)) as Json,
        user: self?.info,
      });
    } catch (error) {
      // Safety net for a genuinely revoked room (a real reconnect/remount race):
      // this runs mid-dispatch, so an uncaught throw would abort the reducer and
      // immer would discard the edit ("changes don't save"). The local Redux
      // mutation + REST autosave are the durable source of truth; the live peer
      // broadcast is best-effort, so swallow and let the edit stand.
      console.warn("[COLLAB] live broadcast skipped:", error);
    }
  }
};
